import React, { useState } from 'react'
import PropTypes from 'prop-types'
import once from 'one-time'

import useLocale from '../../hooks/useLocale'
import { useNavigate } from 'react-router-dom'
import Question from './Question'
import { FormattedMessage } from 'react-intl'
import Arrow from 'media/arrow-back-current-color.svg?svgr-webpack'
import { trackFinishedPlanTest, trackPlanTestStart } from 'js/analytics'

const trackStart = once(trackPlanTestStart)

const PLAN_MASTER_IDS = [
  // Gain
  {
    classic: 20,
    keto_strict: null,
    keto_easy: null,
    five_two: null,
    high_protein: 21,
    clean_eating: 26,
    meal_3wwl: null,
    meal_vegan: 61,
    meal_keto_burn: null,
    meal_keto_maintain: null,
    meal_protein_weightloss: null,
    meal_denice: 70,
    meal_paleo: null,
    meal_sugar_detox: null,
    six_one: null,
    mediterranean: null,
    scandinavian: 33,
  },
  // Maintain
  {
    classic: 18,
    keto_strict: null,
    keto_easy: 16,
    five_two: 17,
    high_protein: 19,
    clean_eating: 25,
    meal_3wwl: null,
    meal_vegan: 61,
    meal_keto_burn: null,
    meal_keto_maintain: 63,
    meal_protein_weightloss: null,
    meal_denice: 70,
    meal_paleo: null,
    meal_sugar_detox: null,
    six_one: 24,
    mediterranean: 37,
    scandinavian: 32,
  },
  // Lose
  {
    classic: 12,
    keto_strict: 8,
    keto_easy: 10,
    five_two: 14,
    high_protein: 11,
    clean_eating: 23,
    meal_3wwl: 47,
    meal_vegan: 61,
    meal_keto_burn: 62,
    meal_keto_maintain: null,
    meal_protein_weightloss: 66,
    meal_denice: 70,
    meal_paleo: 73,
    meal_sugar_detox: 74,
    six_one: 22,
    mediterranean: 34,
    scandinavian: 31,
  },
]

function Questions({ currentQuestion, questions, onNext, onDone }) {
  const isLast = currentQuestion + 1 === questions.length

  return (
    <div
      className="flex transform transition"
      style={{ translate: `-${currentQuestion}00% 0` }}
    >
      {questions.map((question) => {
        if (question.questionId !== currentQuestion) return null

        return (
          <div className="w-full shrink-0" key={question.questionId}>
            <Question
              isLast={isLast}
              question={question}
              onNext={isLast ? onDone : onNext}
            />
          </div>
        )
      })}
    </div>
  )
}

Questions.propTypes = {
  currentQuestion: PropTypes.number,
  questions: PropTypes.array,
  onNext: PropTypes.func,
  onDone: PropTypes.func,
}

export default function PlanQuiz() {
  const { language } = useLocale()
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const navigate = useNavigate()
  const config = require(`./assets/${language}.json`)
  const [planPoints, setPlanPoints] = useState({})
  const [goal, setGoal] = useState()

  const saveAnswer = (newAnswers) => {
    trackStart()

    const newPoints = planPoints
    newAnswers.map((answer) => {
      if (answer.goal) setGoal(answer.goal)

      Object.entries(answer.planPoints).forEach(([key, val]) => {
        newPoints[key] = (newPoints[key] || 0) + val
      })
    })
    setPlanPoints(newPoints)
  }

  const calculateTopPlans = () => {
    trackFinishedPlanTest()

    const sorted = Object.fromEntries(
      Object.entries(planPoints).sort(([, a], [, b]) => b - a),
    )

    return (
      Object.keys(sorted)
        // filter broken plans REMOVE THIS WHEN BACKEND IS DONE
        .filter(
          (plan) =>
            ![
              'meal_vegan',
              'meal_keto_burn',
              'meal_keto_maintain',
              'meal_protein_weightloss',
              'meal_denice',
              'meal_paleo',
              'meal_sugar_detox',
            ].includes(plan),
        )
        .slice(0, 3)
        .map((plan) => PLAN_MASTER_IDS[goal][plan])
    )
  }

  const showResult = () => {
    const topPlans = calculateTopPlans()

    navigate({
      pathname: '/plan-quiz/',
      search: `?plans=${topPlans.toString()}`,
    })
  }

  return (
    <div
      className={`
      mx-auto w-full overflow-hidden rounded-12 border border-Border
      bg-Bg shadow-elevated md:min-w-512 md:max-w-512 lg:rounded-16
    `}
    >
      <div
        className="relative flex flex-col justify-center px-30 text-center"
        style={{ minHeight: 200 }}
      >
        {currentQuestion > 0 && (
          <button
            onClick={() => setCurrentQuestion(currentQuestion - 1)}
            className="absolute left-0 top-0 ml-24 mt-24"
          >
            <Arrow className="text-grey-lighter transition hover:text-Brand" />
            <span className="sr-only">
              <FormattedMessage
                id="planquiz_previous"
                defaultMessage="Previous question"
              />
            </span>
          </button>
        )}
        <h1>
          <span className="type-upper-fat mb-6 block text-BrandDark">
            <FormattedMessage
              id="planTest.diet_quiz_title"
              defaultMessage="Question {currentQuestion} of {questionCount}"
              values={{
                currentQuestion: currentQuestion + 1,
                questionCount: config.questions.length,
              }}
            />
          </span>
          <span className="type-med">
            {config.questions[currentQuestion]?.title}
          </span>
        </h1>
        <div
          className="transition-slower absolute bottom-0 left-0 h-4 bg-Brand transition"
          style={{
            width: `${(currentQuestion / config.questions.length) * 100}%`,
          }}
        />
      </div>
      <Questions
        currentQuestion={currentQuestion}
        questions={config.questions}
        onNext={(answers) => {
          setCurrentQuestion((currentQuestion) => currentQuestion + 1)
          saveAnswer(answers)
        }}
        onDone={() => showResult()}
      />
    </div>
  )
}
